import { makeStyles, styled } from "@material-ui/core";
import {
    Box, Divider,
    FormControl,
    Grid,
    InputLabel, MenuItem, Select
} from "@mui/material";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
    DataGrid,
    GridToolbarColumnsButton,
    GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport,
    GridToolbarFilterButton, GridToolbarQuickFilter
} from '@mui/x-data-grid';
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import UsersListAutocomplete from "../admin/UsersListAutocomplete";
import { DEFAULT_LOCALE, INR_CURRENCY_STYLE } from "../util/constant";
import { getReturnsMonths } from "../util/date-utils";
import HoldingSummary from "./HoldingSummary";
import Positions from "./Positions";

const useStyles = makeStyles(theme => ({
    menuItem: {
        display: 'block',
        blockSize: 'auto'
    },
    profit: {
        color: 'green'
    },
    loss: {
        color: 'red'
    },
    marketScore: {
        backgroundColor: '#1976d2',
        color: 'white'
    },
}))

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .super-app-theme--warning': {
        backgroundColor: '#FFBF00',
    },
    '& .super-app-theme--loss': {
        backgroundColor: '#9d0208',
        color: '#f5acaeff',
    }
}))

export const getClassName = (params, field) => {
    return params.field === field && params.value > 0 ? 'profit' : 'loss';
}

const Holdings = ({ user }) => {
    const currentMonth = moment();

    const history = useHistory();
    const [data, setData] = useState();
    const [rows, setRows] = useState([{ symbol: "" }]);
    const [dbRows, setDbRows] = useState([]);
    const [orderRows, setOrderRows] = useState([]);
    const [strategyWiseProfit, setStrategyWiseProfit] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0.0);
    const [unRealizedProfitOrLossAmount, setUnRealizedProfitOrLossAmount] = useState(0.0);
    const [realizedProfitOrLossAmount, setRealizedProfitOrLossAmount] = useState(0.0);
    const [totalProfitOrLossAmount, setTotalProfitOrLossAmount] = useState(0.0);
    const [marketHolding, setMarketHolding] = useState(0.0);
    const [cash, setCash] = useState(0.0);
    const [firstBought, setFirstBought] = useState();
    const classes = useStyles();
    const [selectedUser, setSelectedUser] = useState("")
    const [selectedMonth, setSelectedMonth] = useState(currentMonth.format("MMM-YYYY").toUpperCase())
    const [percentageReturns, setPercentageReturns] = useState(0)
    const [portfolioRisk, setPortfolioRisk] = useState(0)
    const [portfolioRiskPercentage, setPortfolioRiskPercentage] = useState(0)
    const [months] = useState(getReturnsMonths(currentMonth))

    const resetStates = () => {
        setData(undefined);
        setRows([]);
        setDbRows([]);
        setOrderRows([]);
        setStrategyWiseProfit([]);
        setTotalAmount(0.0);
        setUnRealizedProfitOrLossAmount(0.0);
        setRealizedProfitOrLossAmount(0.0);
        setTotalProfitOrLossAmount(0.0);
        setMarketHolding(0.0);
        setCash(0.0);
        setFirstBought(undefined);
        setPercentageReturns(0);
        setPortfolioRisk(0)
        setPortfolioRiskPercentage(0)
    }
    const handleChange = (user) => {
        resetStates();
        setSelectedUser(user?.id);
    };
    const monthHandleChange = (event) => {
        setSelectedMonth(event.target.value);
    };

    useEffect(() => {
        const endpoint = '/algo-trading/api/demat/profile?userId=' + selectedUser;
        fetch(endpoint, {
            headers: new Headers({
                'Authorization': user.credential
            }),
        }
        ).then(r => {
            if (r.status >= 400) {
                history.push('/')
                return
            }
            r.text()
                .then(value => {
                    setData(JSON.parse(value));
                })
        }
        ).catch(error => {
            console.log(error);
            history.push('/')
        });
    }, [selectedUser]);

    useEffect(() => {
        const endpoint = '/algo-trading/api/demat/returns?userId=' + selectedUser + '&month=' + selectedMonth;
        fetch(endpoint, {
            headers: new Headers({
                'Authorization': user.credential
            }),
        }
        ).then(r => {
            if (r.status >= 400) {
                history.push('/')
                return
            }
            r.text()
                .then(value => {
                    setPercentageReturns(parseFloat(value))
                })
        }
        ).catch(error => {
            console.log(error);
            history.push('/')
        });
    }, [selectedUser, selectedMonth]);

    useEffect(() => {
        let realized = data?.realizedProfit?.earnings ? data?.realizedProfit?.earnings : 0.0
        let unrRealized = data?.unrRealizedProfit ? data?.unrRealizedProfit : 0.0
        setUnRealizedProfitOrLossAmount(unrRealized)
        setRealizedProfitOrLossAmount(realized)
        setTotalProfitOrLossAmount(unrRealized + realized)
        setTotalAmount(data?.investedAmount)
        setFirstBought(data?.realizedProfit?.firstBought)
        setMarketHolding(data?.holding)
        setCash(data?.cash)
        setRows(data?.holdings)
        setDbRows(data?.openDBTrades)
        setStrategyWiseProfit(data?.strategyWiseProfit)
        setOrderRows(data?.orderBooks)
        if (data?.holdings?.length > 0 && data?.holding > 0) {
            let total = data?.holdings?.reduce((total, h) => { return total + ((h?.ltp - h?.triggerSL) * h?.quantity); }, 0);
            setPortfolioRisk(total)
            setPortfolioRiskPercentage(total / (data?.holding + data?.cash) * 100)
        }
    }, [data]);


    const holdingColumns = [
        {
            field: 'broker', headerName: 'Broker', width: 100, type: 'string',
        },
        {
            field: 'symbol', headerName: 'Symbol', width: 140, type: 'string',
            renderCell: (params) => (
                <a href={"https://www.tradingview.com/chart/?symbol=NSE:" + params.value} target="_blank">
                    {params.value}
                </a>
            ),
        },
        { field: 'quantity', headerName: 'Quantity', width: 80 },
        {
            field: 'sharePrice', headerName: 'Buy Price', width: 100,
            valueFormatter: ({ value }) => value?.toFixed(2)
        },
        {
            field: 'buyValue', headerName: 'Buy Value', width: 100,
            valueGetter: (params) => {
                return params?.row?.sharePrice * params?.row?.quantity
            },
            renderCell: ({ value }) => value?.toLocaleString(DEFAULT_LOCALE, INR_CURRENCY_STYLE)
        },
        { field: 'ltp', headerName: 'LTP', width: 100, renderCell: ({ value }) => value?.toFixed(2) },
        {
            field: 'profitLoss', headerName: 'PnL', width: 90, type: 'number',
            renderCell: ({ value }) => value?.toLocaleString(DEFAULT_LOCALE, INR_CURRENCY_STYLE)
        },
        {
            field: 'pnlPercentage', headerName: 'PnL %', width: 70, type: 'number',
            renderCell: ({ value }) => value?.toFixed(2)
        },
        {
            field: 'triggerSL', headerName: 'Trigger SL', width: 90, type: 'number',
        },
        {
            field: 'openDays', headerName: 'Open Days', width: 90, type: 'number',
        },
        {
            field: 'percentagePerDay', headerName: 'PPD', width: 80, type: 'number',
            renderCell: ({ value }) => value?.toFixed(2)
        },
        {
            field: 'percentagePerDayInLast15Days', headerName: 'PPD15', width: 80, type: 'number',
            renderCell: ({ value }) => value?.toFixed(2)
        },
        {
            field: 'riskPrice', headerName: 'Risk Price', width: 80, type: 'number',
            valueGetter: (params) => {
                return params?.row?.ltp - params?.row?.triggerSL
            },
            renderCell: ({ value }) => value?.toFixed(2)
        },
        {
            field: 'riskAmount', headerName: 'Risk Amount', width: 100, type: 'number',
            valueGetter: (params) => {
                return (params?.row?.ltp - params?.row?.triggerSL) * params?.row?.quantity
            },
            renderCell: ({ value }) => value?.toFixed(2)
        },
        {
            field: 'slDistance%', headerName: 'SL Distance %', width: 80, type: 'number',
            valueGetter: (params) => {
                return ((params?.row?.ltp - params?.row?.triggerSL) * params?.row?.quantity) / (params?.row?.ltp * params?.row?.quantity) * 100
            },
            renderCell: ({ value }) => value?.toFixed(2) + ' %'
        },
        {
            field: 'risk%', headerName: 'Risk %', width: 80, type: 'number',
            valueGetter: (params) => {
                return ((params?.row?.ltp - params?.row?.triggerSL) * params?.row?.quantity) / marketHolding * 100
            },
            renderCell: ({ value }) => value?.toFixed(2) + ' %'
        },
    ];
    const dbColumns = [
        { field: 'symbol', headerName: 'Symbol', width: 120, type: 'string' },
        { field: 'strategyDisplayName', headerName: 'Strategy', width: 150, type: 'string' },
        { field: 'buyQuantity', headerName: 'Buy Quantity', width: 100 },
        { field: 'buySharePrice', headerName: 'Buy Price', width: 100 },
        { field: 'boughtOn', headerName: 'Bought On', width: 160 },
        { field: 'closed', headerName: 'Closed', width: 100 },
        { field: 'sellSharePrice', headerName: 'Sell Price', width: 100 },
        { field: 'soldOn', headerName: 'Sold On', width: 160 },
        { field: 'earnings', headerName: 'Earnings', width: 120, valueFormatter: ({ value }) => value?.toLocaleString(DEFAULT_LOCALE, INR_CURRENCY_STYLE) },
        { field: 'earningsPercentage', headerName: 'Earnings %', width: 90, valueFormatter: ({ value }) => `${value == null ? '' : value + ' %'}` },
        { field: 'boughtDayLowPrice', headerName: 'Bought Day Low', width: 120, valueFormatter: ({ value }) => value?.toFixed(2) },
        { field: 'previousClosingPrice', headerName: 'Yesterday Close', width: 120, valueFormatter: ({ value }) => value?.toFixed(2) },
        { field: 'last15DaysHigh', headerName: 'Last 15 Days High', width: 150, valueFormatter: ({ value }) => value?.toFixed(2) },
        { field: 'logicalSL', headerName: 'Logical SL', width: 150 },
        { field: 'highAfterBought', headerName: 'High After Bought', width: 150, valueFormatter: ({ value }) => value?.toFixed(2) },
        { field: 'priceBefore15Days', headerName: 'Price Before 15 days', width: 150, valueFormatter: ({ value }) => value?.toFixed(2) },
        { field: 'exitReason', headerName: 'Exit Reason', width: 150 },
    ];
    const orderColumns = [
        { field: 'broker', headerName: 'Broker', width: 70, type: 'string' },
        { field: 'tradingsymbol', headerName: 'Symbol', width: 200, type: 'string' },
        { field: 'ordertype', headerName: 'Order Type', width: 100, type: 'string' },
        { field: 'transactiontype', headerName: 'Action', width: 60 },
        { field: 'status', headerName: 'Status', width: 100 },
        { field: 'quantity', headerName: 'Quantity', width: 70 },
        { field: 'averageprice', headerName: 'Avg Price', width: 100 },
        { field: 'triggerprice', headerName: 'Trigger Price', width: 100 },
        { field: 'exchtime', headerName: 'Traded On', width: 160 },
        { field: 'orderid', headerName: 'Order Id', width: 150 },
        { field: 'ordertag', headerName: 'Tag', width: 100 },
        { field: 'text', headerName: 'Text', width: 500 },
    ];
    function CustomToolbar(tableName) {
        return (
            <GridToolbarContainer title={tableName} sx={{ justifyContent: 'flex-start' }}>
                <Typography variant="h6">{tableName}</Typography>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
                <GridToolbarExport />
                <GridToolbarQuickFilter />
            </GridToolbarContainer>
        );
    }

    return (
        <>
            <Grid item xs={12}>
                <Typography variant="h4" gutterBottom component="div">
                    Demat Profile
                </Typography>
            </Grid>
            <UsersListAutocomplete loggedInUser={user} selectedUser={selectedUser} setSelectedUser={handleChange} />
            <Grid item xs={12} md={4}>
                <HoldingSummary cash={cash}
                    holding={marketHolding}
                    firstBought={firstBought}
                    marktScore={data?.marketScore}
                    realizedProfitOrLossAmount={realizedProfitOrLossAmount}
                    totalAmount={totalAmount}
                    totalProfitOrLossAmount={totalProfitOrLossAmount}
                    unRealizedProfitOrLossAmount={unRealizedProfitOrLossAmount}
                    classes={classes}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <Paper elevation={2}>
                    <Box component="span" display="flex" flexDirection="column">
                        <Typography variant="subtitle2" align="center">Total Realized trades ({data?.realizedSummary?.totalRealizedTrades})</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" m={1} sx={{
                        flexDirection: { xs: 'row', md: 'row' }, align: { xs: 'center', md: 'left' },
                        backgroundColor: '#1976d2', color: 'white'
                    }}>
                        <Box component="span" display="flex" flexDirection="column">
                            <Typography variant="caption">Win Rate ({data?.realizedSummary?.winTrades})</Typography>
                            <Typography variant="subtitle2">{data?.realizedSummary?.winRate?.toFixed(2)} %</Typography>
                        </Box>
                        <Divider />
                        <Box component="span" display="flex" flexDirection="column">
                            <Typography variant="caption">Average Win</Typography>
                            <Typography variant="subtitle2">{data?.realizedSummary?.averageWinPercentage?.toFixed(2)} %</Typography>
                        </Box>
                        <Divider />
                        <Box component="span" display="flex" flexDirection="column">
                            <Typography variant="caption">Average Loss</Typography>
                            <Typography variant="subtitle2">{data?.realizedSummary?.averageLossPercentage?.toFixed(2)} %</Typography>
                        </Box>
                    </Box>
                    <Box display="flex" justifyContent="space-between" m={1} sx={{
                        flexDirection: { xs: 'row', md: 'row' }, align: { xs: 'center', md: 'left' }
                    }}>
                        <Box component="span" display="flex" flexDirection="column">
                            <Typography variant="caption">Avg Win Holding Days</Typography>
                            <Typography variant="subtitle2">{data?.realizedSummary?.avgHoldingWinDays}</Typography>
                        </Box>
                        <Divider />
                        <Box component="span" display="flex" flexDirection="column">
                            <Typography variant="caption">Avg Loss Holding Days</Typography>
                            <Typography variant="subtitle2">{data?.realizedSummary?.avgHoldingLossDays}</Typography>
                        </Box>
                        <Divider />
                    </Box>
                </Paper>
                <Paper elevation={2}>
                    <Box display="flex" justifyContent="space-between" m={1} sx={{
                        flexDirection: { xs: 'row', md: 'row' }, align: { xs: 'center', md: 'left' }
                    }}>
                        <Box component="span" display="flex" flexDirection="column">
                            <FormControl fullWidth>
                                <InputLabel id="select-user">Month</InputLabel>
                                <Select
                                    labelId="select-month"
                                    id="month-select"
                                    value={selectedMonth}
                                    label="Month"
                                    onChange={monthHandleChange}
                                    autowidth="true"
                                >
                                    {months.map((month, index) =>
                                        <MenuItem key={month.id} className={classes.menuItem}
                                            value={month.value}>{month.label}</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box component="span" display="flex" flexDirection="column">
                            <Typography variant="caption">Portfolio Monthly Returns %</Typography>
                            <Typography variant="subtitle2">{percentageReturns?.toFixed(2)} %</Typography>
                        </Box>
                    </Box>
                    <Divider />
                </Paper>
            </Grid>
            <Grid container display='flex' flexDirection='row' spacing={2}>
                <Grid item xs={12} md={6} lg={6}>
                    <Box
                        sx={{
                            '& .profit': {
                                color: 'green',
                            },
                            '& .loss': {
                                color: 'red',
                            },
                            '& .negative': {
                                backgroundColor: 'rgba(157, 255, 118, 0.49)',
                                color: '#1a3e72',
                                fontWeight: '600',
                            },
                            '& .positive': {
                                backgroundColor: '#d47483',
                                color: '#1a3e72',
                                fontWeight: '600',
                            },
                            '& .warning': {
                                color: '#FFBF00',
                            }
                        }}
                    >
                        {rows && <StyledDataGrid
                            autoHeight={true}
                            getRowId={(row) => row?.symbol}
                            rows={rows}
                            columns={holdingColumns}
                            initialState={{
                                density: 'compact',
                                sorting: {
                                    sortModel: [{ field: 'percentagePerDay', sort: 'asc' }],
                                },
                                pagination: {
                                    paginationModel: {
                                        pageSize: 25
                                    }
                                }
                            }}
                            getCellClassName={(params) => {
                                if (params.field !== 'profitLoss') {
                                    return '';
                                }
                                return getClassName(params, 'profitLoss')
                            }}
                            getRowClassName={params => {
                                const ltp = params?.row?.ltp
                                const triggerSL = params?.row?.triggerSL

                                if (ltp < triggerSL)
                                    return 'super-app-theme--loss'

                                // Here ltp is >= triggerSL
                                // If LTP is 2% close to triggerSL then show amber color
                                // E.g. if triggerSL is 100 and LTP is <= 102
                                const twoPercentOfTriggerSL = triggerSL * 0.02;
                                if ((ltp - triggerSL) <= twoPercentOfTriggerSL)
                                    return 'super-app-theme--warning'

                                return ''
                            }}
                            slots={{
                                toolbar: () =>
                                (<GridToolbarContainer style={{ justifyContent: 'space-around' }}>
                                    <Typography variant='h6' align='center'>Holdings</Typography>
                                    <Typography variant='subtitle2' align='center'>Portfolio risk ({portfolioRisk?.toLocaleString(DEFAULT_LOCALE, INR_CURRENCY_STYLE)} / {portfolioRiskPercentage.toFixed(2)}%)</Typography>
                                    <GridToolbarColumnsButton />
                                    <GridToolbarFilterButton />
                                    <GridToolbarDensitySelector />
                                    <GridToolbarExport />
                                    <GridToolbarQuickFilter />
                                </GridToolbarContainer>)
                            }}
                            sx={{ bgcolor: 'white' }}
                        />}
                    </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                    <Positions loggedInUser={user} selectedUserId={selectedUser} source="holdings" />
                </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
                <Grid>
                    <Box
                        sx={{
                            height: 550,
                            width: '100%',
                            '& .profit': {
                                color: 'green',
                            },
                            '& .loss': {
                                color: 'red',
                            },
                        }}
                    >
                        {orderRows && <DataGrid
                            getRowId={(row) => row?.orderid}
                            rows={orderRows}
                            columns={orderColumns}
                            initialState={{
                                filter: {
                                    filterModel: {
                                        items: [{ field: 'status', operator: 'equals', value: 'complete' }],
                                    },
                                },
                                pagination: {
                                    paginationModel: {
                                        pageSize: 25
                                    }
                                }
                            }}
                            slots={{
                                toolbar: () => CustomToolbar('Order Book'),
                            }}
                            sx={{ bgcolor: 'white' }}
                        />}
                    </Box>
                </Grid>
            </Grid>
            {
                dbRows && dbRows.length > 0 && <Grid item xs={12} md={6}>
                    <Grid>
                        <Box
                            sx={{
                                height: 550,
                                width: '100%',
                                '& .profit': {
                                    color: 'green',
                                },
                                '& .loss': {
                                    color: 'red',
                                },
                            }}
                        >
                            <DataGrid
                                getRowId={(row) => row?.tradeId}
                                rows={dbRows}
                                columns={dbColumns}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: 'boughOn', sort: 'desc' }],
                                    },
                                    filter: {
                                        filterModel: {
                                            items: [{ field: 'closed', operator: 'equals', value: 'false' }],
                                        },
                                    },

                                    pagination: {
                                        paginationModel: {
                                            pageSize: 25
                                        }
                                    }
                                }}
                                getCellClassName={(params) => {
                                    if (params.field === 'earnings') {
                                        return getClassName(params, 'earnings')
                                    }
                                    else if (params.field === 'earningsPercentage') {
                                        return getClassName(params, 'earningsPercentage')
                                    }
                                    return '';
                                }}
                                slots={{
                                    toolbar: () => CustomToolbar('DB Trades'),
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            }
        </>
    )
}

export default Holdings